import { cn } from "@/utils/shadcn";
import { Button } from "@/components/shadcn/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/shadcn/card";
import { Input } from "@/components/shadcn/input";
import { constants } from "@/constants";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { isPlatform, platformMostAI, platformWizyChat } from "@/domains.ts";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/shadcn/form.tsx";
import { OtpForm } from "@/pages/auth/OtpForm.tsx";
import Logo from "@/components/partials/header/Tools/Logo";
import Branding from "@/utils/branding.ts";

const FormSchema = z.object({
  email: z.string().email(),
});

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const { t } = useTranslation();

  const [otpMode, setOtpMode] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(
    !isPlatform(platformMostAI),
  );
  const [email, setEmail] = useState<string | null>(null);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: "",
    },
  });

  let googleAuthURL = constants.url.SIGN_IN_URL;
  if (!isPlatform(platformWizyChat)) {
    googleAuthURL = `https://wizy.chat/api/v1/auth/google?source=site&domain=${window.location.hostname}`;
  }

  function onSubmit(data: z.infer<typeof FormSchema>) {
    setEmail(data.email);
    setOtpMode(true);
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      {!otpMode && (
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">
              <div className="w-full flex items-center justify-center py-2">
                <Logo />
              </div>
              {t("login_page.title")}
            </CardTitle>
            <CardDescription></CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {isPlatform(platformMostAI) && (
                <a
                  className="px-6 py-3 font-semibold text-gray-900 bg-white transition-colors border border-gray-200 rounded-md shadow-md outline-none hover:bg-slate-50 inline-flex items-center justify-center"
                  href={`${constants.url.API_PREFIX}auth/yandex?source=site`}
                >
                  <img src="/account/logo/yandex.svg" className="h-8 mr-3" />
                  Войти через Яндекс
                </a>
              )}

              {Branding().googleAuthEnabled && (
                <>
                  <div className="flex flex-col gap-4">
                    <a
                      className="px-6 py-3 font-semibold text-gray-900 bg-white transition-colors border border-gray-200 rounded-md shadow-md outline-none hover:bg-slate-50 inline-flex items-center justify-center"
                      href={googleAuthURL}
                    >
                      <img
                        src="icons/flat-color-icons--google.svg"
                        alt=""
                        className="inline w-8 h-8 mr-3 text-gray-900 fill-current"
                      />
                      {t("login_page.sign_in_with_google")}
                    </a>
                  </div>
                  <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                    <span className="relative z-10 bg-background px-2 text-muted-foreground">
                      {t("login_page.or_continue_with")}
                    </span>
                  </div>
                </>
              )}
              <div className="grid">
                {!showEmailInput && (
                  <div
                    className="text-center cursor-pointer"
                    onClick={() => {
                      setShowEmailInput(true);
                    }}
                  >
                    <div className="px-6 py-3 bg-white transition-colors border border-gray-200 rounded-md shadow-md outline-none hover:bg-slate-50 flex items-center justify-center font-semibold text-gray-900">
                      <img
                        src="icons/fluent--mail-24-regular.svg"
                        alt=""
                        className="inline w-8 h-8 mr-3 text-gray-900 fill-current"
                      />
                      {t("login_page.sign_in_with_email")}
                    </div>
                  </div>
                )}
                {showEmailInput && (
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmit)}
                      className="w-full space-y-6"
                    >
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("login_page.your_email")}</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="contact@gmail.com"
                              />
                            </FormControl>
                            <FormDescription></FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        variant="default"
                        type="submit"
                        className="w-full"
                      >
                        {t("login_page.sign_in")}
                      </Button>
                    </form>
                  </Form>
                )}
                <div className="pt-1 text-sm text-center opacity-70">
                  {t("login_page.sign_in_with_email_desc")}
                </div>
              </div>
            </div>
          </CardContent>
          {isPlatform(platformMostAI) && (
            <CardFooter className="block opacity-60 pt-8 text-center">
              <a
                href="https://most-ai.com/privacy-agreement"
                target="_blank"
                className="underline block"
              >
                Политика конфиденциальности
              </a>
              <a
                href="https://most-ai.com/offer"
                target="_blank"
                className="underline block"
              >
                Пользовательское соглашение
              </a>
            </CardFooter>
          )}
        </Card>
      )}

      {otpMode && email && (
        <OtpForm
          onClose={() => {
            setOtpMode(false);
          }}
          email={email}
        />
      )}
    </div>
  );
}
